import React, { useState } from 'react';
import { Button, Card, Alert, Spinner, Table, Badge, Container } from 'react-bootstrap';
import { useWorkspaces } from '../../hooks/useWorkspaces';
import { useCalendar } from '../../hooks/useCalendar';
import { RefreshCw, Trash2, Plus, CheckCircle, Lock } from 'lucide-react';
import CalendarConnectionDialog from './CalendarConnectionDialog';
import { userService } from '../../services/userService';

const CalendarIntegrations = () => {
    const { selectedWorkspace: activeWorkspace } = useWorkspaces({});
    const {
        connectedCalendars,
        loadConnectedCalendars,
        disconnectCalendar,
        syncExternalCalendar
    } = useCalendar({ workspaceId: activeWorkspace?.id });

    // Subscription Check
    const [isPaid, setIsPaid] = useState(false);
    const [loadingSub, setLoadingSub] = useState(true);

    React.useEffect(() => {
        const checkSub = async () => {
            try {
                const sub = await userService.getSubscription();
                const paidPlans = ['Premium', 'Unlimited'];
                setIsPaid(paidPlans.includes(sub?.plan_name) || sub?.price_per_user_cents > 0);
            } catch (e) { console.error(e); }
            finally { setLoadingSub(false); }
        };
        checkSub();
    }, []);

    // Load list on mount
    React.useEffect(() => {
        loadConnectedCalendars();
    }, [loadConnectedCalendars]);

    const [showDialog, setShowDialog] = useState(false);
    const [headerSuccess, setHeaderSuccess] = useState(null);
    const [headerError, setHeaderError] = useState(null);
    const [syncingId, setSyncingId] = useState(null);

    // Auto-dismiss success message
    React.useEffect(() => {
        if (headerSuccess) {
            const timer = setTimeout(() => setHeaderSuccess(null), 5000);
            return () => clearTimeout(timer);
        }
    }, [headerSuccess]);

    const handleSync = async (calendarId) => {
        setSyncingId(calendarId);
        setHeaderSuccess(null);
        setHeaderError(null);
        try {
            await syncExternalCalendar(calendarId);
            await loadConnectedCalendars();
            setHeaderSuccess("Calendar synced successfully!");
        } catch (err) {
            console.error(err);
            setHeaderError("Calendar sync failed. Please check your connection and try again.");
        } finally {
            setSyncingId(null);
        }
    };

    const handleDisconnect = async (calendarId) => {
        if (window.confirm("Are you sure you want to disconnect this calendar?")) {
            try {
                await disconnectCalendar(calendarId);
                setHeaderSuccess("Calendar disconnected.");
            } catch (err) {
                console.error(err);
            }
        }
    };

    return (
        <Container fluid className="p-0">
            {headerSuccess && (
                <Alert variant="success" onClose={() => setHeaderSuccess(null)} dismissible className="mb-3">
                    <CheckCircle size={18} className="me-2" />
                    {headerSuccess}
                </Alert>
            )}
            {headerError && (
                <Alert variant="danger" onClose={() => setHeaderError(null)} dismissible className="mb-3">
                    {headerError}
                </Alert>
            )}

            <Card className="shadow-sm">
                <Card.Header className="bg-white d-flex justify-content-between align-items-center py-3">
                    <h5 className="mb-0">Connected Calendars</h5>
                    {loadingSub ? (
                        <Spinner size="sm" />
                    ) : isPaid ? (
                        <Button variant="primary" size="sm" onClick={() => setShowDialog(true)}>
                            <Plus size={16} className="me-1" />
                            Add Calendar
                        </Button>
                    ) : (
                        <Button variant="outline-secondary" size="sm" disabled title="Upgrade to Premium">
                            <Lock size={14} className="me-1" />
                            Pro Feature
                        </Button>
                    )}
                </Card.Header>
                <Card.Body className="p-0">
                    {connectedCalendars && connectedCalendars.length > 0 ? (
                        <Table hover responsive className="mb-0 align-middle">
                            <thead className="bg-light">
                                <tr>
                                    <th className="ps-4">Provider</th>
                                    <th>Workspace</th>
                                    <th>URL / Server</th>
                                    <th>Event Details</th>
                                    <th>Last Synced</th>
                                    <th>Status</th>
                                    <th className="text-end pe-4">Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {connectedCalendars.map(conf => (
                                    <tr key={conf.id}>
                                        <td className="ps-4">
                                            <Badge bg="secondary" className="text-uppercase px-2 py-1">{conf.provider}</Badge>
                                        </td>
                                        <td>
                                            {conf.workspace_name}
                                        </td>
                                        <td className="text-truncate" style={{ maxWidth: '250px' }} title={conf.calendar_url}>
                                            {conf.calendar_url}
                                        </td>
                                        <td>
                                            {conf.settings?.obfuscate ? (
                                                <Badge bg="dark">Hidden</Badge>
                                            ) : (
                                                <Badge bg="light" text="dark" className="border">Visible</Badge>
                                            )}
                                        </td>
                                        <td>
                                            {conf.last_synced_at ? (
                                                <small className="text-muted">
                                                    {new Date(conf.last_synced_at).toLocaleString()}
                                                </small>
                                            ) : (
                                                <small className="text-muted">-</small>
                                            )}
                                        </td>
                                        <td>
                                            <Badge bg={conf.last_synced_at ? "success" : "warning"} pill>
                                                {conf.last_synced_at ? "Synced" : "Pending"}
                                            </Badge>
                                        </td>
                                        <td className="text-end pe-4">
                                            <Button
                                                variant="light"
                                                size="sm"
                                                className="me-2 text-primary border-0"
                                                onClick={() => handleSync(conf.id)}
                                                disabled={syncingId === conf.id}
                                                title="Sync Now"
                                            >
                                                {syncingId === conf.id ? (
                                                    <Spinner size="sm" animation="border" variant="primary" />
                                                ) : (
                                                    <RefreshCw size={18} />
                                                )}
                                            </Button>
                                            <Button
                                                variant="light"
                                                size="sm"
                                                className="text-danger border-0"
                                                onClick={() => handleDisconnect(conf.id)}
                                                title="Disconnect"
                                            >
                                                <Trash2 size={18} />
                                            </Button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </Table>
                    ) : (
                        <div className="text-center py-5 text-muted">
                            <p className="mb-0">No external calendars connected yet.</p>
                            <p className="small">Click "Add Calendar" to get started.</p>
                        </div>
                    )}
                </Card.Body>
            </Card>

            <CalendarConnectionDialog
                show={showDialog}
                onHide={() => setShowDialog(false)}
                onSuccess={() => {
                    loadConnectedCalendars();
                    setHeaderSuccess("New calendar connected successfully!");
                }}
                workspaceId={activeWorkspace?.id}
            />
        </Container>
    );
};

export default CalendarIntegrations;
